<template>
  <div>
    <b-form-row>
      <!-- Column 1: CheckID -->
      <b-col :cols="headerColumnSpan">
        <b-form-group
          v-if="externalSurveyInstanceId"
          :label="$t('Webportal.SurveyInstanceId')"
          label-size="sm"
        >
          <span class="font-weight-bold">{{ externalSurveyInstanceId }}</span>

          <template v-if="surveyRevision && relatedSurveyRevisionsVisible">
            <b-button
              id="popover-related-survey-revisions"
              class="ml-2"
              size="sm"
              variant="outline-primary">
              <font-awesome-icon :icon="['fas', 'link']" />
            </b-button>
            <b-popover
              target="popover-related-survey-revisions"
              triggers="hover focus"
              placement="bottom"
              custom-class="mw-100"
              :title="$t('Webportal.Actions.RelatedSurveyRevisions')">
              <b-list-group v-if="surveyRevision.RelatedSurveyRevisions && surveyRevision.RelatedSurveyRevisions.length > 0" flush>
                <b-list-group-item
                  v-for="(relatedSurveyRevision, index) in surveyRevision.RelatedSurveyRevisions"
                  :key="index">
                  <b-row>
                    <b-col cols="auto">
                      <b-link :to="{ name: 'SurveyRevision', params: { id: relatedSurveyRevision.SurveyRevisionId } }" target="_blank">
                        #{{ relatedSurveyRevision.SurveyRevisionVisibleId }}
                      </b-link>
                    </b-col>
                    <b-col cols="auto">
                      {{ relatedSurveyRevision.LastUpdatedAt | msm_datetime }}
                    </b-col>
                    <b-col>
                      <span class="font-weight-bold">
                        <font-awesome-icon
                          :icon="['far', getRevisionStatusIcon(relatedSurveyRevision.StatusId)]"
                        />
                        {{ getRevisionStatusText(relatedSurveyRevision.StatusId) }}
                      </span>
                    </b-col>
                  </b-row>
                </b-list-group-item>
              </b-list-group>
              <span v-else>
                {{ $t("Webportal.NoDataFoundPlain") }}
              </span>
            </b-popover>
          </template>
        </b-form-group>
        <!-- Show Customer Name here on top when there is no associated check -->
        <b-form-group v-else :label="$t('Webportal.Customer')" label-size="sm">
          {{ surveyRevision.ClientName }}
        </b-form-group>
      </b-col>

      <!-- Column 2: Survey -->
      <b-col :cols="headerColumnSpan">
        <b-form-group
          v-if="surveyInstance"
          :label="$t('Webportal.Survey')"
          label-size="sm"
        >
          {{ surveyInstance.Survey.Title }}
        </b-form-group>
      </b-col>

      <!-- Column 3: Status -->
      <b-col class="ml-auto" :cols="headerColumnSpan">
        <b-form-group
          v-if="statusId != null"
          :label="$t('Webportal.Status')"
          label-size="sm"
        >
          <div class="d-flex flex-wrap" style="gap:0.5rem;">
            <b-dropdown
              v-if="isStatusSelectionVisible"
              size="sm"
              variant="primary"
              link-class="font-weight-bold"
              :disabled="statusSelectionDisabled"
              v-b-tooltip.hover="toolTipDelay" :title="statusSelectionDisabled ? $t('Webportal.PleaseSaveChanges') : ''"
            >
              <template #button-content>
                <span class="font-weight-bold">
                  <font-awesome-icon
                    :icon="['far', getRevisionStatusIcon(statusId)]"
                  /> {{ getRevisionStatusText(statusId) }}</span>
              </template>
              <b-dropdown-item
                v-for="nextStatus in surveyRevision.NextAllowedSurveyRevisionStatuses"
                :key="nextStatus"
                :variant="
                  nextStatus == SurveyRevisionStatus.Dismissed ? 'danger' : null
                "
                @click="nextStatusClicked(nextStatus)"
              >
                <font-awesome-icon
                  :icon="['far', getRevisionStatusIcon(nextStatus)]"
                />
                {{ getRevisionStatusText(nextStatus) }}
              </b-dropdown-item>
            </b-dropdown>

            <span v-else class="font-weight-bold">
              <template v-if="surveyRevision">
                <font-awesome-icon
                  :icon="['far', getRevisionStatusIcon(statusId)]"
                />
                {{ getRevisionStatusText(statusId) }}
              </template>
              <template v-else>
                {{ getSurveyInstanceStatusText(statusId) }}
              </template>
            </span>

            <template v-if="surveyRevision && statusHistoryVisible">
              <b-button
                id="popover-status-history"
                size="sm"
                variant="outline-primary"
              >
                <font-awesome-icon :icon="['fas', 'history']" />
              </b-button>
              <b-popover
                target="popover-status-history"
                triggers="hover focus"
                placement="bottom"
                custom-class="mw-100"
                :title="$t('Webportal.StatusHistory')"
              >
                <b-list-group
                  v-if="
                    surveyRevision.History && surveyRevision.History.length > 0
                  "
                  flush
                >
                  <b-list-group-item
                    v-for="(history, index) in surveyRevision.History"
                    :key="index"
                  >
                    <b-row>
                      <b-col>
                        <span class="font-weight-bold">
                          <font-awesome-icon
                            :icon="[
                              'far',
                              getRevisionStatusIcon(history.StatusId),
                            ]"
                          />
                          {{ getRevisionStatusText(history.StatusId) }}
                        </span>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col cols="auto">
                        {{ history.ModifiedAt | msm_datetime }}
                      </b-col>
                      <b-col>
                        {{
                          `${history.ModifiedBy.FirstName} ${history.ModifiedBy.LastName}` +
                          (history.ModifiedBy.Email
                            ? ` (${history.ModifiedBy.Email})`
                            : "")
                        }}
                      </b-col>
                    </b-row>
                  </b-list-group-item>
                </b-list-group>
                <span v-else>
                  {{ $t("Webportal.NoDataFoundPlain") }}
                </span>
              </b-popover>
            </template>
          </div>
        </b-form-group>
      </b-col>

      <!-- Column 4 (Complaint only): Reason Selection -->
      <b-col v-if="isReasonSelectionVisible || isCustomFlag1Visible">
        <b-form-group  v-if="isReasonSelectionVisible"
          :label="$t('Webportal.Actions.SurveyRevision.Reason')"
          label-size="sm"
        >
          <b-dropdown
            id="reasons-selection"
            size="sm"
            :variant="isReasonSelected ? 'primary' : 'outline-primary'"
            link-class="font-weight-bold"
            class="mw-100"
            toggle-class="mw-100"
            :disabled="reasonSelectionDisabled"
            >
            <template #button-content>
              <span v-if="!isReasonSelected || surveyRevision.Reasons.length == 1"
                :class="{ 'font-weight-bold': isReasonSelected }"
                style="max-width: 95%; display: inline-block; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; vertical-align: middle;">
                {{ isReasonSelected ? surveyRevision.Reasons[0] : $t('Webportal.Actions.SurveyRevision.Reason.Placeholder') }}
              </span>
              <span v-else>
                {{ $t('Webportal.Actions.SurveyRevision.ReasonsSelected', { count: surveyRevision.Reasons.length }) }}
              </span>
            </template>
            <b-dropdown-item
              v-for="(reason, index) in reasonOptions"
              :key="`reason_${index}`"
              @click="reasonSelected(reason)"
            >
              <div class="d-inline-block" style="width:20px;">
                <font-awesome-icon v-if="surveyRevision?.Reasons?.includes(reason)" icon="check" />
              </div>
              {{ reason }}
            </b-dropdown-item>
          </b-dropdown>
          <b-popover v-if="isReasonSelected" 
            target="reasons-selection"
            triggers="hover"
            :delay="300"
            placement="bottom"
            custom-class="mw-100">
            <b-list-group flush>
              <b-list-group-item v-for="(reason, index) in surveyRevision.Reasons" :key="index">
                <span class="font-weight-bold">
                  {{ reason }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </b-form-group>

        <!-- Column 5 (Complaint only): Custom Flag 1 -->
        <b-form-group v-if="isCustomFlag1Visible" label-size="sm">
          <template #label>
            {{ $t('Webportal.Actions.SurveyRevision.CustomFlag1') }}
            <span v-if="$te('Webportal.Actions.SurveyRevision.CustomFlag1.Description')"
              v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Actions.SurveyRevision.CustomFlag1.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </template>
          <label class="switch switch-label switch-secondary">
            <input class="switch-input" type="checkbox"
                  v-model="surveyRevision.CustomFlag1"
                  :disabled="customFlag1Disabled"
                  @change="customFlag1Changed($event.target.checked)">
            <span class="switch-slider"></span>
          </label>
        </b-form-group>
      </b-col>

      <!-- Column 4 (General Inquiry only): Assigned To Role Selection -->
      <b-col v-if="isAssignedToRoleSelectionVisible">
        <b-form-group
          :label="$t('Webportal.Actions.SurveyRevision.AssignedToRole')"
          label-size="sm"
        >
          <b-dropdown
            v-if="!assignedToRoleSelectionReadonly"
            size="sm"
            :variant="surveyRevision.AssignedToRole ? 'primary' : 'outline-primary'"
            link-class="font-weight-bold"
            :disabled="isAssignedToRoleSelectionDisabled"
            v-b-tooltip.hover="toolTipDelay" :title="isAssignedToRoleSelectionDisabled ? $t('Webportal.PleaseSaveChanges') : ''"
          >
            <template #button-content>
              <span class="font-weight-bold">
                <font-awesome-icon
                  :icon="['fas', getRevisionRoleIcon(surveyRevision.AssignedToRole)]"
                  fixed-width
                />
                {{ getRevisionRoleText(surveyRevision.AssignedToRole) }}
              </span>
            </template>
            <b-dropdown-item
              v-for="option in allowedRevisionRoleOptions"
              :key="option.value"
              @click="assignedToRoleSelected(option.value)"
            >
              <font-awesome-icon
                :icon="['fas', getRevisionRoleIcon(option.value)]"
                fixed-width
              />
              {{ option.text }}
            </b-dropdown-item>
          </b-dropdown>

          <span v-else class="font-weight-bold">
            <font-awesome-icon
              :icon="['far', getRevisionRoleIcon(surveyRevision.AssignedToRole)]"
              fixed-width
            />
            {{ getRevisionRoleText(surveyRevision.AssignedToRole) }}
          </span>
        </b-form-group>
      </b-col>
    </b-form-row>

    <b-form-row v-if="surveyInstance">
      <!-- Column 1: Customer and Location ID -->
      <b-col :cols="headerColumnSpan * 2">
        <b-form-row>
          <b-col>
            <b-form-group :label="$t('Webportal.Customer')" label-size="sm">
              {{ surveyInstance.Client.Name }}
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group :label="$t('Webportal.LocationId')" label-size="sm">
              {{ surveyInstance.ClientLocation.ClientLocationIdOnClientSite }}
            </b-form-group>
          </b-col>
        </b-form-row>
        <p v-if="!surveyRevision && notificationHint">
          <span class="text-danger">
            <font-awesome-icon :icon="['fas', 'info-circle']" /> {{ notificationHint }}:
          </span>
          <b-link :to="notificationSettingsRoute">{{ $t("Webportal.NotificationSettings") }}</b-link>
        </p>
      </b-col>
      <b-col>
        <b-form-group :label="$t('Webportal.LocationAddress')" label-size="sm">
          {{ surveyInstance.ClientLocation.Name }}<br />
          {{ surveyInstance.ClientLocation.Address }}<br />
          <span v-if="surveyInstance.ClientLocation.Address2">
            {{ surveyInstance.ClientLocation.Address2 }}
            <br />
          </span>
          {{ surveyInstance.ClientLocation.City }},
          {{ surveyInstance.ClientLocation.CountryId }} -
          {{ surveyInstance.ClientLocation.PostalCode }}
        </b-form-group>
      </b-col>
      <b-col v-if="isResultAdjustmentOverviewVisible" :cols="headerColumnSpan">
        <b-form-group :label="$t('Webportal.Actions.SurveyRevision.ResultAdjustment')" label-size="sm">
          <b-row v-for="(item, index) in resultAdjustmentItems" :key="item.label">
            <b-col class="text-truncate" :class="{ 'ml-2': index > 0 }">
              {{ item.label }}
            </b-col>
            <b-col cols="2" class="font-weight-bold text-right">
              {{ item.value }}
            </b-col>
          </b-row>
        </b-form-group>
      </b-col>
    </b-form-row>

    <div v-if="isResultAdjustmentOverviewVisible && !isMsmUser" v-html="$t('Webportal.Actions.SurveyRevision.ResultAdjustmentNote')"></div>
  </div>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import SurveyInstanceMixin from '@/mixins/SurveyInstanceMixin';
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";

export default {
  name: "SurveyRevisionHeader",

  mixins: [ AppConfig, SurveyInstanceMixin, SurveyRevisionMixin ],

  props: {
    surveyRevision: null,
    surveyInstance: null,
    statusSelectionVisible: { default: false },
    statusSelectionDisabled: { default: false },
    statusHistoryVisible: { default: true },
    relatedSurveyRevisionsVisible: { default: true },
    reasons: { default: null },
    reasonSelectionVisible: { default: false },
    reasonSelectionDisabled: { default: false },
    customFlag1Visible: { default: false },
    customFlag1Disabled: { default: false },
    assignedToRoleSelectionVisible: { default: false },
    assignedToRoleSelectionDisabled: { default: false },
    assignedToRoleSelectionReadonly: { default: false },
    resultAdjustmentOverviewVisible: { default: false },
  },

  computed: {
    headerColumnSpan() {
      return this.isReasonSelectionVisible || this.isAssignedToRoleSelectionVisible
        || this.isResultAdjustmentOverviewVisible || this.isCustomFlag1Visible ? 3 : 4;
    },

    externalSurveyInstanceId() {
      return (
        this.surveyRevision?.ExternalSurveyInstanceId ??
        this.surveyInstance?.ExternalSurveyInstanceId ??
        this.surveyInstance?.SurveyInstance?.ExternalSurveyInstanceId
      );
    },

    statusId() {
      return (
        this.surveyRevision?.StatusId ??
        this.surveyInstance?.SurveyInstance?.SurveyInstanceStatusId
      );
    },

    isStatusSelectionVisible() {
      return (
        this.statusSelectionVisible && !_.isEmpty(this.surveyRevision?.NextAllowedSurveyRevisionStatuses)
      );
    },

    notificationHint() {
      return this.$t('Webportal.Actions.SurveyRevision.NotificationNotice');
    },

    isReasonSelected() {
      return this.surveyRevision?.Reasons?.length > 0;
    },

    isReasonSelectionVisible() {
      return this.surveyRevision && this.reasonSelectionVisible && ((this.reasons && this.reasons.length > 0) || this.surveyRevision.Reasons);
    },

    reasonOptions() {
      const reasons = this.reasons ?? [];

      return _.union(reasons, this.surveyRevision?.Reasons ?? []);
    },

    isCustomFlag1Visible() {
      return this.surveyRevision && this.customFlag1Visible;
    },

    isAssignedToRoleSelectionVisible() {
      return this.surveyRevision && this.assignedToRoleSelectionVisible;
    },

    isAssignedToRoleSelectionDisabled() {
      return this.assignedToRoleSelectionDisabled;
    },

    isResultAdjustmentOverviewVisible() {
      return this.surveyRevision && this.resultAdjustmentOverviewVisible;
    },

    resultAdjustmentItems() {
      return [
        {
          label: this.$t('Webportal.Actions.SurveyRevision.QuestionCount'),
          value: this.surveyRevision.Entries.length
        },
        {
          label: this.$t('Webportal.Actions.SurveyRevision.QuestionWithResultAdjustmentCount'),
          value: this.surveyRevision.Entries.filter(e => this.resultAdjustmentMap.get(e.WasResultAdjusted) === true).length
        },
        {
          label: this.$t('Webportal.Actions.SurveyRevision.QuestionWithoutResultAdjustmentCount'),
          value: this.surveyRevision.Entries.filter(e => this.resultAdjustmentMap.get(e.WasResultAdjusted) === false).length
        },
      ];
    },

    allowedRevisionRoleOptions() {
      return this.revisionRoleOptions.filter(option => option.value !== this.surveyRevision.AssignedToRole);
    }
  },

  methods: {
    onStatusChanged(value) {
      this.$emit("statusChanged", value);
    },

    reasonSelected(reason) {
      let reasonsValue = this.surveyRevision.Reasons ?? [];
      
      if (reasonsValue.includes(reason)) {
        reasonsValue = reasonsValue.filter(r => r !== reason);
      } else {
        reasonsValue.push(reason);
      }

      this.$emit('input', reasonsValue);
    },

    assignedToRoleSelected(role) {
      this.$emit('assignedToRoleChanged', role);
    },

    customFlag1Changed(checked) {
      this.$emit('customFlag1Changed', checked ? 1 : 0);
    },

    nextStatusClicked(nextStatusId) {
      const isFinishedState = this.surveyRevision.FinishedSurveyRevisionStatuses?.includes(nextStatusId);

      if (this.isReasonSelectionVisible && !this.isReasonSelected && isFinishedState) {
        this.$bvModal
          .msgBoxOk(this.$t("Webportal.Dialog.NoReasonSelected.Text"),
          {
            title: this.$t("Webportal.Dialog.NoReasonSelected.Title"),
            okTitle: this.$t("Webportal.Dialog.Ok"),
            hideHeaderClose: false,
          });
        return;
      }

      // Show confirmation dialog before setting final status
      if (
        nextStatusId == this.SurveyRevisionStatus.Approved ||
        nextStatusId == this.SurveyRevisionStatus.Dismissed ||
        nextStatusId == this.SurveyRevisionStatus.DismissedCustom1 ||
        nextStatusId == this.SurveyRevisionStatus.DismissedCustom2 ||
        nextStatusId == this.SurveyRevisionStatus.DismissedCustom3 ||
        nextStatusId == this.SurveyRevisionStatus.Revised ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApproved ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApprovedGoodwill ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApprovedCustom1 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApprovedCustom2 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApprovedCustom3 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedDismissed ||
        nextStatusId == this.SurveyRevisionStatus.RevisedDismissedCustom1 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedDismissedCustom2 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedDismissedCustom3 ||
        nextStatusId == this.SurveyRevisionStatus.Done ||
        nextStatusId == this.SurveyRevisionStatus.Published
      ) {
        const h = this.$createElement;

        this.$bvModal
          .msgBoxConfirm(
            [
              h("div", {
                domProps: {
                  innerHTML: this.$t(
                    "Webportal.Dialog.ConfirmStatusChange.Text",
                    {
                      status: `<b>${this.getRevisionStatusText(
                        nextStatusId
                      )}</b>`,
                    }
                  ),
                },
              }),
            ],
            {
              title: this.$t("Webportal.Dialog.ConfirmStatusChange.Title"),
              okTitle: this.$t("Webportal.Dialog.Ok"),
              cancelTitle: this.$t("Webportal.Dialog.Cancel"),
              hideHeaderClose: false,
            }
          )
          .then((value) => {
            if (value) {
              this.onStatusChanged(nextStatusId);
            }
          });
      } else this.onStatusChanged(nextStatusId);
    },
  },
};
</script>
